import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Zap, MapPin, Calendar, Phone, Mail, CheckCircle, Clock, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminHotshots = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  useAdminAuth();
  const [requests, setRequests] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchRequests(); }, []);
  useEffect(() => {
    setFiltered(filter === 'all' ? requests : requests.filter(r => r.status === filter));
  }, [requests, filter]);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/hotshot-requests`);
      setRequests(res.data);
    } catch { toast({ title: 'Error', description: 'Failed to load hotshot requests', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/hotshot-requests/${id}/status`, { status });
      toast({ title: 'Success', description: `Status updated to ${status}` });
      fetchRequests();
    } catch { toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' }); }
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    confirmed: requests.filter(r => r.status === 'confirmed').length,
    completed: requests.filter(r => r.status === 'completed').length
  };

  if (loading) return <div className="min-h-screen bg-gray-50"><Header /><div className="container mx-auto px-4 py-12 text-center"><p>Loading...</p></div><Footer /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#1e3a5f]">Hotshot Requests</h1>
              <p className="text-gray-600 mt-2">Manage urgent hotshot delivery requests</p>
            </div>
            <Button onClick={() => navigate('/admin')} variant="outline">Back to Dashboard</Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-5"><p className="text-sm text-gray-600">Total</p><p className="text-3xl font-bold text-[#1e3a5f]">{stats.total}</p></Card>
            <Card className="p-5"><p className="text-sm text-gray-600">Pending</p><p className="text-3xl font-bold text-yellow-600">{stats.pending}</p></Card>
            <Card className="p-5"><p className="text-sm text-gray-600">Confirmed</p><p className="text-3xl font-bold text-green-600">{stats.confirmed}</p></Card>
            <Card className="p-5"><p className="text-sm text-gray-600">Completed</p><p className="text-3xl font-bold text-purple-600">{stats.completed}</p></Card>
          </div>

          <Card className="p-4 mb-6">
            <Label>Filter by Status</Label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(s => (
                <Button key={s} variant={filter === s ? 'default' : 'outline'} onClick={() => setFilter(s)} size="sm" className="capitalize">{s === 'all' ? 'All' : s}</Button>
              ))}
            </div>
          </Card>

          <div className="space-y-4">
            {filtered.length === 0 ? (
              <Card className="p-12 text-center"><Zap className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-600">No hotshot requests found</p></Card>
            ) : filtered.map(req => (
              <Card key={req.id} className="p-6 border-l-4 border-l-red-500" data-testid={`hotshot-req-${req.id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : req.status === 'confirmed' ? 'bg-green-100 text-green-800' : req.status === 'completed' ? 'bg-purple-100 text-purple-800' : 'bg-red-100 text-red-800'}`}>{req.status}</span>
                      <span className="text-xs text-gray-500">{req.date_time_required}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{req.name} {req.company_name && `— ${req.company_name}`}</h3>
                    <div className="grid md:grid-cols-2 gap-3 mb-3 text-sm">
                      <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-green-600 mt-0.5" /><div><p className="text-xs text-gray-500">Pickup</p><p className="font-medium">{req.pickup_address}</p><p className="text-xs text-gray-500">{req.pickup_contact_name} — {req.pickup_contact_phone}</p></div></div>
                      <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-red-600 mt-0.5" /><div><p className="text-xs text-gray-500">Delivery</p><p className="font-medium">{req.delivery_address}</p><p className="text-xs text-gray-500">{req.delivery_contact_name} — {req.delivery_contact_phone}</p></div></div>
                      <div className="flex items-start gap-2"><Mail className="w-4 h-4 text-gray-500 mt-0.5" /><div><p className="text-xs text-gray-500">Email</p><p className="font-medium">{req.email}</p></div></div>
                      <div className="flex items-start gap-2"><Phone className="w-4 h-4 text-gray-500 mt-0.5" /><div><p className="text-xs text-gray-500">Phone</p><p className="font-medium">{req.phone}</p></div></div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
                      <p><strong>Load:</strong> {req.load_description}</p>
                      <p><strong>Weight:</strong> {req.weight} | <strong>Dimensions:</strong> {req.dimensions}</p>
                    </div>
                    {req.message && <p className="text-sm text-gray-600 italic mb-2">"{req.message}"</p>}
                    <p className="text-xs text-gray-400">ID: {req.id} | Created: {new Date(req.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex flex-col gap-2 ml-4">
                    {req.status === 'pending' && (<><Button size="sm" onClick={() => updateStatus(req.id, 'confirmed')} className="bg-green-600 hover:bg-green-700 text-white">Confirm</Button><Button size="sm" variant="outline" onClick={() => updateStatus(req.id, 'cancelled')} className="text-red-600 border-red-300">Cancel</Button></>)}
                    {req.status === 'confirmed' && <Button size="sm" onClick={() => updateStatus(req.id, 'completed')} className="bg-purple-600 hover:bg-purple-700 text-white">Complete</Button>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminHotshots;
